import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
  signal,
} from '@angular/core';
import { MatIcon } from '@angular/material/icon';
import {
  Estimate,
  VendorOrder,
  VendorQuote,
} from '../../../../../../shared/models/vendor-order.model';
import { QuoteCardComponent } from '../quote-card/quote-card.component';
import { QuoteType } from '../quote-types';

@Component({
  selector: 'app-previous-quotes',
  imports: [MatIcon, QuoteCardComponent],
  templateUrl: './previous-quotes.component.html',
  styleUrl: './previous-quotes.component.scss',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PreviousQuotesComponent {
  readonly estimates = input.required<Estimate[]>();
  readonly order = input.required<VendorOrder>();
  readonly selectedEstimate = input<Estimate | null>(null);
  readonly lowestCostId = input<string | null>(null);
  readonly showTopBorder = input(false);

  readonly isExpanded = signal(false);
  readonly previousSelectedQuoteType = computed<QuoteType | null>(() => {
    const selectedEstimate =
      this.selectedEstimate() ?? this.findSelectedVendorEstimate();
    if (!selectedEstimate?.quote?.reanalysis) return null;

    return this.getSelectedQuoteType(selectedEstimate);
  });

  readonly previousSelectedEstimate = computed<Estimate | null>(() => {
    const selectedEstimate =
      this.selectedEstimate() ?? this.findSelectedVendorEstimate();
    const quote = selectedEstimate?.quote;

    const selectedQuoteType = this.previousSelectedQuoteType();
    if (!selectedEstimate || !quote?.reanalysis || !selectedQuoteType) {
      return null;
    }

    const previousQuote: VendorQuote = {
      acceptedBy: quote.acceptedBy,
      acceptedDate: quote.acceptedDate,
      normal: quote.normal,
      rushed: quote.rushed,
    };

    return {
      ...selectedEstimate,
      quote: previousQuote,
    };
  });

  readonly displayEstimates = computed(() => {
    const estimates = [...this.estimates()];
    const previousSelectedEstimate = this.previousSelectedEstimate();
    if (previousSelectedEstimate) {
      estimates.push(previousSelectedEstimate);
    } else {
      const unselectedEstimate = this.createUnselectedEstimate();
      if (unselectedEstimate) {
        estimates.push(unselectedEstimate);
      }
    }

    return estimates;
  });

  readonly isLowestRate = (estimate: Estimate): boolean => {
    const id = estimate.vendorId ?? estimate.estimationsId ?? null;
    if (!id) return false;
    return id === this.lowestCostId();
  };

  readonly trackEstimate = (estimate: Estimate, index: number): string => {
    const vendorId =
      estimate.vendorId ??
      estimate.workflow_uid ??
      estimate.estimationsId ??
      `index-${index}`;
    const normalCost = estimate.quote?.normal?.totalCost ?? 'none';
    const rushedCost = estimate.quote?.rushed?.totalCost ?? 'none';
    const previous = estimate === this.previousSelectedEstimate();
    return `${vendorId}-${normalCost}-${rushedCost}-${
      previous ? 'previous' : 'quote'
    }`;
  };

  toggleSection(): void {
    this.isExpanded.update((v) => !v);
  }

  private createUnselectedEstimate(): Estimate | null {
    const selectedEstimate =
      this.selectedEstimate() ?? this.findSelectedVendorEstimate();
    const quote = selectedEstimate?.quote;

    if (!selectedEstimate || !quote?.normal || !quote?.rushed) {
      return null;
    }

    const rushedSelected = this.resolveRushedSelected(selectedEstimate);
    const quoteType = rushedSelected ? 'normal' : 'rushed';
    const unselectedQuote = quote[quoteType];

    if (!unselectedQuote) {
      return null;
    }

    return {
      ...selectedEstimate,
      quote: this.buildSingleQuote(quoteType, unselectedQuote),
    };
  }

  private findSelectedVendorEstimate(): Estimate | null {
    const orderVendorId = this.order().vendorId;
    const estimates = this.order().estimates ?? this.estimates();

    if (orderVendorId) {
      const match = estimates.find(
        (estimate) =>
          orderVendorId === estimate.vendorId ||
          orderVendorId === estimate.workflow_uid ||
          orderVendorId === estimate.estimationsId,
      );
      if (match) {
        return match;
      }
    }

    return (
      estimates.find(
        (estimate) =>
          !!estimate.quote?.acceptedBy && !!estimate.quote?.acceptedDate,
      ) ?? null
    );
  }

  private buildSingleQuote(
    quoteType: 'normal' | 'rushed',
    quote: NonNullable<VendorQuote['normal']>,
  ): VendorQuote {
    return quoteType === 'normal' ? { normal: quote } : { rushed: quote };
  }

  private getSelectedQuoteType(estimate: Estimate): QuoteType | null {
    const quote = estimate.quote;
    if (!quote) return null;

    if (this.resolveRushedSelected(estimate) && quote.rushed) {
      return 'Rushed';
    }
    if (quote.normal) {
      return 'Standard';
    }
    return quote.rushed ? 'Rushed' : null;
  }

  /** Returns true when the rushed delivery type was selected for this estimate. */
  private resolveRushedSelected(est: Estimate): boolean {
    return est.rushedDateSelected === true;
  }
}
